import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import RawResponsePanel from "@/components/RawResponsePanel";
import {
  Search, Loader2, Eye, Activity, ShieldCheck, Receipt, Wallet, Store, User,
  ChevronRight, ChevronLeft, ExternalLink, Save,
} from "lucide-react";

const KV = ({ label, value, mono = false, className = "" }) => (
  <div className={className}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`mt-0.5 text-sm ${mono ? "font-mono break-all" : ""}`}>
      {value == null || value === "" ? <em className="text-muted-foreground">—</em> : value}
    </div>
  </div>
);

const PAGE_SIZE = 50;

export default function AdminAllTxns() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [channelId, setChannelId] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const [channels, setChannels] = useState([]);
  const [picked, setPicked] = useState(null);

  // Load channels list for filter dropdown
  useEffect(() => {
    api.get("/admin/channels").then(({ data }) => {
      setChannels(data.channels || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (type) params.set("type", type);
      if (status) params.set("status", status);
      if (channelId) params.set("channel_id", channelId);
      if (needsReview) params.set("needs_review", "1");
      params.set("limit", PAGE_SIZE);
      params.set("skip", skip);
      const { data } = await api.get(`/admin/all-txns?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load transactions");
    } finally {
      setLoading(false);
    }
  }, [q, type, status, channelId, needsReview, skip]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: open the detail dialog automatically when ?txn=<id> is present.
  // The id can be either the internal UUID or the display_id (e.g. BBP-2026...).
  // We hit the same detail endpoint either way — the backend already accepts both.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("txn");
    if (!wanted || picked) return;
    setPicked({ id: wanted });
  }, [picked]);

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">All transactions</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every BBPS / Payout / Top-up txn in the system. Click any row to see the full audit trail
              including the outbound API request + response bodies.
            </p>
          </div>
          <Badge variant="outline" className="text-sm">{total.toLocaleString()} total</Badge>
        </div>

        {/* Filters */}
        <div className="surface-card p-3 mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by ID / display ID / provider ref / UTR / mobile / consumer ID…"
              value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }}
              className="pl-8 text-sm font-mono"
              data-testid="all-txn-search"
            />
          </div>
          <select
            value={type} onChange={(e) => { setSkip(0); setType(e.target.value); }}
            className="text-sm rounded-md border border-input bg-background px-2 py-1.5"
            data-testid="all-txn-type-filter"
          >
            <option value="">All types</option>
            <option value="bbps">BBPS</option>
            <option value="payout">Payout</option>
            <option value="topup">Top-up</option>
            <option value="wallet">Wallet ledger</option>
          </select>
          <select
            value={status} onChange={(e) => { setSkip(0); setStatus(e.target.value); }}
            className="text-sm rounded-md border border-input bg-background px-2 py-1.5"
            data-testid="all-txn-status-filter"
          >
            <option value="">All statuses</option>
            {["success", "pending", "failed", "pending_l1", "pending_l2", "pending_l3", "rejected", "resubmit_requested"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {channels.length > 0 && (
            <select
              value={channelId} onChange={(e) => { setSkip(0); setChannelId(e.target.value); }}
              className="text-sm rounded-md border border-input bg-background px-2 py-1.5"
              data-testid="all-txn-channel-filter"
            >
              <option value="">All channels</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>{ch.name} ({ch.code})</option>
              ))}
            </select>
          )}
          <Button
            size="sm"
            variant={needsReview ? "default" : "outline"}
            onClick={() => { setSkip(0); setNeedsReview((v) => !v); }}
            data-testid="all-txn-needs-review-toggle"
            title="Past txns with missing bank / network / pay-mode mapping"
          >
            Needs review {needsReview ? "·on" : ""}
          </Button>
        </div>

        {/* Table */}
        <div className="surface-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Display ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Category / Rail</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Provider ref / UTR</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-muted-foreground">No transactions match these filters.</TableCell></TableRow>
              ) : items.map((t) => (
                <TableRow key={t.id} className="cursor-pointer hover:bg-accent/40" onClick={() => setPicked(t)} data-testid={`all-txn-row-${t.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-xs">{t.display_id || t.id?.slice(0, 8)}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        t.type === "wallet"
                          ? `text-[10px] ${t.wallet_kind === "credit" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`
                          : "text-[10px]"
                      }
                    >
                      {t.type === "wallet" ? `Wallet ${t.wallet_kind || ""}`.trim() : t.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{(t.category || t.method || "—").replace?.(/_/g, " ") || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{t.customer_mobile || (t.type === "wallet" ? (t.user_id?.slice(0, 8) || "—") : "—")}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {t.type === "wallet" && t.wallet_kind === "debit" ? "−" : ""}{formatINR(t.amount)}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{t.status}</Badge></TableCell>
                  <TableCell className="text-xs font-mono">
                    {t.provider_ref || t.bank_utr || t.ref_id || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {t.channel_code ? (
                      <Badge variant="outline" className="text-[9px] font-mono">{t.channel_code}</Badge>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setPicked(t); }}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-3 text-sm">
          <div className="text-xs text-muted-foreground">
            Showing {skip + 1}–{Math.min(skip + items.length, total)} of {total.toLocaleString()}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <Button size="sm" variant="outline" disabled={skip + PAGE_SIZE >= total} onClick={() => setSkip(skip + PAGE_SIZE)}>
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <FullDetailDialog
        picked={picked}
        onClose={() => setPicked(null)}
        onPivot={(refId, refType) => {
          // Pivot to the linked txn — re-uses the dialog by swapping `picked`.
          // The new useEffect run will fetch /full for the new id.
          setPicked({ id: refId, type: refType || "bbps", display_id: refId });
        }}
      />
    </DashboardLayout>
  );
}


function FullDetailDialog({ picked, onClose, onPivot }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadFull = useCallback(async () => {
    if (!picked) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/admin/all-txns/${picked.id}/full`);
      setFull(data);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Could not load full audit";
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  }, [picked]);

  useEffect(() => {
    if (!picked) { setFull(null); setError(null); return; }
    let cancel = false;
    (async () => {
      try { await loadFull(); } catch { /* handled */ }
      if (cancel) return;
    })();
    return () => { cancel = true; };
  }, [picked, loadFull]);

  const refreshDetail = loadFull;

  if (!picked) return null;
  const t = full?.transaction || picked;

  return (
    <Dialog open={!!picked} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono flex items-center gap-3 flex-wrap">
            <span>{t.display_id || t.id?.slice(0, 8)} · {t.type?.toUpperCase()}</span>
            {/* Phase 7.30h pivot CTA — only for wallet ledger rows that point
                at a real BBPS/payout txn or a wallet top-up. */}
            {full?.kind === "wallet_ledger" && t.ref_id && ["txn", "transaction", "bbps", "payout", "topup", "wallet_topup"].includes(String(t.ref_type || "")) && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7 ml-auto"
                onClick={() => {
                  const isTopup = ["topup", "wallet_topup"].includes(String(t.ref_type));
                  onPivot?.(t.ref_id, isTopup ? "topup" : "bbps");
                }}
                data-testid="pivot-linked-txn-btn"
                title={`Open ${t.ref_type || "linked"} record ${t.ref_id}`}
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                View linked {String(t.ref_type || "txn").replace(/_/g, " ")}
              </Button>
            )}
          </DialogTitle>
          <DialogDescription>
            Full audit trail: txn + partner + customer + KYC + linked instrument + every outbound API call.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : error ? (
          <div className="py-12 text-center space-y-3" data-testid="audit-error-state">
            <div className="text-destructive text-sm">{error}</div>
            <div className="text-xs text-muted-foreground">
              This is normal for some row types (e.g. legacy entries without a full audit trail).
              The row data shown above the dialog is still complete.
            </div>
            <Button variant="outline" size="sm" onClick={onClose} data-testid="audit-close-btn">Close</Button>
          </div>
        ) : !full ? null : (
          <Tabs defaultValue="txn">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="txn">Txn ({Object.keys(full.transaction || {}).length})</TabsTrigger>
              <TabsTrigger value="partner">Partner</TabsTrigger>
              <TabsTrigger value="customer">Customer</TabsTrigger>
              <TabsTrigger value="instrument">Instrument</TabsTrigger>
              <TabsTrigger value="api">API calls ({full.api_log_count || 0})</TabsTrigger>
              <TabsTrigger value="raw">Raw JSON</TabsTrigger>
            </TabsList>

            <TabsContent value="txn" className="mt-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 surface-card p-4">
                <KV label="Amount" value={formatINR(t.amount)} mono />
                <KV label="Status" value={t.status} />
                <KV label="Type" value={t.type} />
                <KV label="Category / Rail" value={t.category || t.method} />
                <KV label="Created" value={new Date(t.created_at).toLocaleString()} />
                <KV label="Updated" value={t.updated_at ? new Date(t.updated_at).toLocaleString() : "—"} />
                <KV label="Provider ref" value={t.provider_ref} mono />
                <KV label="Bank UTR" value={t.bank_utr} mono />
                <KV label="Platform fee" value={formatINR(t.platform_fee || 0)} mono />
                <KV label="PG fee" value={formatINR(t.pg_fee || 0)} mono />
                <KV label="Net earnings" value={formatINR(t.net_earnings || 0)} mono />
                <KV label="Wallet debit" value={formatINR(t.wallet_debit || 0)} mono />
                {t.type === "bbps" && (
                  <>
                    <KV label="Biller" value={t.biller_name} />
                    <KV label="Consumer ID" value={t.consumer_id} mono />
                    <KV label="Consumer name" value={t.consumer_name} />
                  </>
                )}
                {t.type === "payout" && (
                  <>
                    <KV label="Method" value={t.method} />
                    <KV label="Beneficiary" value={(t.beneficiary_snapshot || {})?.nickname || "—"} />
                  </>
                )}
              </div>
              {Array.isArray(t.approval_chain) && t.approval_chain.length > 0 && (
                <div className="surface-card p-4">
                  <div className="text-sm font-semibold mb-2">Approval timeline</div>
                  <ol className="space-y-2">
                    {t.approval_chain.map((e, i) => (
                      <li key={i} className="text-xs border-l-2 border-primary/30 pl-3 ml-1">
                        <div className="font-semibold">{e.event || e.action}</div>
                        <div className="text-muted-foreground">{e.at && new Date(e.at).toLocaleString()} · {e.by_email || e.approver_email || e.by}</div>
                        {(e.note || e.remarks) && <div className="italic mt-0.5">"{e.note || e.remarks}"</div>}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {/* Payment mode details from gateway + inline manual mapping */}
              <PaymodeSection txn={t} onUpdated={refreshDetail} />
              {/* Raw gateway response — same payload as the unmapped queue. */}
              <RawResponsePanel data={t.pg_payment_details} testId="raw-gateway-response" />
            </TabsContent>

            <TabsContent value="partner" className="mt-4 space-y-3">
              <div className="surface-card p-4">
                <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Store className="h-4 w-4" /> Profile</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  <KV label="Business" value={full.partner?.business_name} />
                  <KV label="Name" value={full.partner?.full_name} />
                  <KV label="Email" value={full.partner?.email} mono />
                  <KV label="Mobile" value={full.partner?.mobile} mono />
                  <KV label="Role" value={full.partner?.role} />
                  <KV label="Wallet ₹" value={formatINR(full.partner?.wallet_balance || 0)} mono />
                </div>
              </div>
              <div className="surface-card p-4">
                <div className="text-sm font-semibold mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> KYC</div>
                {!full.partner_kyc ? (
                  <div className="text-sm text-muted-foreground">No KYC on file.</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    <KV label="Status" value={full.partner_kyc.status} />
                    <KV label="PAN" value={full.partner_kyc.pan} mono />
                    <KV label="Aadhaar" value={full.partner_kyc.aadhaar_masked || full.partner_kyc.aadhaar} mono />
                    <KV label="Address" value={full.partner_kyc.address} className="sm:col-span-2 lg:col-span-3" />
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="customer" className="mt-4 space-y-3">
              {!t.customer_mobile && !full.customer ? (
                <div className="surface-card p-4 text-sm text-muted-foreground">
                  <User className="h-4 w-4 inline mr-1 opacity-50" /> No customer linked (self-mode or partner-funded).
                </div>
              ) : (
                <>
                  <div className="surface-card p-4">
                    <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Wallet className="h-4 w-4" /> Wallet</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      <KV label="Mobile" value={full.customer?.mobile || t.customer_mobile} mono />
                      <KV label="Balance" value={formatINR(full.customer?.balance || 0)} mono />
                      <KV label="KYC name" value={full.customer_kyc?.full_name} />
                      <KV label="KYC status" value={full.customer_kyc?.status} />
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="instrument" className="mt-4">
              <div className="surface-card p-4">
                {!full.instrument ? (
                  <div className="text-sm text-muted-foreground">No instrument linked.</div>
                ) : (
                  <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-muted/40 p-3 rounded max-h-[60vh] overflow-auto">
                    {JSON.stringify(full.instrument, null, 2)}
                  </pre>
                )}
              </div>
            </TabsContent>

            <TabsContent value="api" className="mt-4 space-y-2">
              {(!full.api_logs || full.api_logs.length === 0) ? (
                <div className="surface-card p-6 text-sm text-muted-foreground text-center">
                  <Activity className="h-5 w-5 mx-auto mb-1 opacity-50" />
                  No outbound API logs match this txn's identifiers ({(full.match_keys || []).map((k, i) => <code key={i} className="mx-1 font-mono">{k}</code>)}).
                </div>
              ) : (
                full.api_logs.map((l, i) => (
                  <ApiLogReplay key={l.id || i} log={l} />
                ))
              )}
            </TabsContent>

            <TabsContent value="raw" className="mt-4">
              <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-muted/40 p-4 rounded border border-border max-h-[70vh] overflow-auto">
                {JSON.stringify(full, null, 2)}
              </pre>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── ApiLogReplay — one collapsible log card with optional "Replay" button.
const SAFE_REPLAY_PATTERNS = [
  "billpayment/status",
  "payout_status",
  "payoutstatus",
  "pgstatus",
  "getordertxn",
  "verifyorder",
  "getfetch",
  "getbillpaymentplan",
  "account/profile",
  "wallet/balance",
];
const isSafeToReplay = (url = "") => {
  const u = url.toLowerCase();
  return SAFE_REPLAY_PATTERNS.some((p) => u.includes(p));
};

function ApiLogReplay({ log }) {
  const [replayResult, setReplayResult] = React.useState(null);
  const [replaying, setReplaying] = React.useState(false);
  const safe = isSafeToReplay(log.request_url || log.url || "");

  const doReplay = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Re-issue this API call to the provider now?")) return;
    setReplaying(true);
    try {
      const { data } = await api.post(`/admin/all-txns/api-logs/${log.id}/replay`, { confirm: true });
      setReplayResult(data);
      toast.success(`Replayed · HTTP ${data.new_response?.status ?? "—"}${data.ok ? "" : " (failed)"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Replay failed");
    } finally {
      setReplaying(false);
    }
  };

  return (
    <details className="surface-card p-3 text-xs" data-testid={`api-log-${log.id}`}>
      <summary className="cursor-pointer font-mono flex items-baseline gap-2 flex-wrap">
        <Badge variant="outline" className="text-[10px]">{log.method || "POST"}</Badge>
        <span className="text-[10px] text-muted-foreground">{new Date(log.created_at).toLocaleString()}</span>
        <span className="font-semibold truncate max-w-[400px]">{log.request_url || log.url || log.endpoint}</span>
        <Badge variant="outline" className={`text-[10px] ${log.response_status >= 200 && log.response_status < 300 ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"}`}>
          {log.response_status || "—"}
        </Badge>
        <span className="text-[10px] text-muted-foreground">{log.elapsed_ms != null ? `${log.elapsed_ms}ms` : ""}</span>
        {safe ? (
          <Button
            size="sm" variant="outline" className="ml-auto h-6 text-[10px] px-2"
            onClick={doReplay} disabled={replaying}
            data-testid={`replay-${log.id}`}
            title="Re-issue this exact request to the provider (safe / idempotent endpoint)"
          >
            {replaying ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
            Replay
          </Button>
        ) : (
          <span className="ml-auto text-[10px] text-muted-foreground italic" title="Replay blocked — this endpoint has financial side-effects (e.g. initiate, transfer).">no replay</span>
        )}
      </summary>
      <div className="mt-2 grid sm:grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Request</div>
          <pre className="font-mono text-[10px] bg-background p-2 rounded whitespace-pre-wrap break-all max-h-72 overflow-auto">
            {JSON.stringify(log.request_body || log.request, null, 2) || "—"}
          </pre>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Response</div>
          <pre className="font-mono text-[10px] bg-background p-2 rounded whitespace-pre-wrap break-all max-h-72 overflow-auto">
            {JSON.stringify(log.response_body || log.response, null, 2) || "—"}
          </pre>
        </div>
      </div>
      {log.error && (
        <div className="mt-2 text-[10px] text-destructive italic">Error: {log.error}</div>
      )}
      {replayResult && (
        <div className="mt-3 border-t border-border pt-3">
          <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-1">
            Replay result · HTTP {replayResult.new_response?.status ?? "—"} · {replayResult.new_response?.elapsed_ms}ms
          </div>
          <pre className="font-mono text-[10px] bg-primary/5 p-2 rounded whitespace-pre-wrap break-all max-h-72 overflow-auto">
            {JSON.stringify(replayResult.new_response?.body || replayResult.new_response?.error || {}, null, 2)}
          </pre>
        </div>
      )}
    </details>
  );
}

// Re-export Receipt to avoid eslint dead-import warning in some environments.
export { Receipt as _PageReceiptIcon };


/**
 * Inline manual mapping for a single past txn.
 * Renders the existing read-only "Payment mode" KV grid AND, when something is
 * unresolved/missing, three dropdowns letting the admin pick a master record
 * for bank / card_network / pay_mode. On save, the raw value (if any) is
 * also stored as an alias so identical raws on future txns auto-resolve.
 */
function PaymodeSection({ txn, onUpdated }) {
  const [masters, setMasters] = useState({ bank: [], card_network: [], pay_mode: [] });
  const [pickBank, setPickBank] = useState("");
  const [pickNet, setPickNet] = useState("");
  const [pickMode, setPickMode] = useState("");
  const [addAliases, setAddAliases] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cats = ["bank", "card_network", "pay_mode"];
        const out = {};
        await Promise.all(cats.map(async (c) => {
          const { data } = await api.get(`/admin/master-data/categories/${c}`);
          out[c] = (data.records || []).filter((r) => r.status !== "inactive");
        }));
        setMasters((m) => ({ ...m, ...out }));
      } catch { /* silent */ }
    })();
    setPickBank(""); setPickNet(""); setPickMode("");
  }, [txn?.id]);

  const hasAnyPg = !!(txn.pg_mode_type || txn.pg_card_network || txn.pg_bank_name || txn.pg_payment_details);
  if (!hasAnyPg) return null;

  // A txn "needs review" when at least one of bank / network / mode is missing
  const isResolvedBank = !!(txn.pg_bank_name && masters.bank.some((m) => m.name?.toLowerCase() === String(txn.pg_bank_name).toLowerCase()));
  const isResolvedNet  = !!(txn.pg_card_network && masters.card_network.some((m) => m.name?.toLowerCase() === String(txn.pg_card_network).toLowerCase()));
  const isResolvedMode = !!(txn.pg_mode_type && masters.pay_mode.some((m) => m.name?.toLowerCase() === String(txn.pg_mode_type).toLowerCase()));
  const needsReview = !(isResolvedBank && isResolvedNet && isResolvedMode);

  const save = async () => {
    if (!pickBank && !pickNet && !pickMode) {
      toast.error("Pick at least one master record to apply");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/all-txns/${txn.id}/map-paymode`, {
        master_bank_id: pickBank || null,
        master_network_id: pickNet || null,
        master_mode_id: pickMode || null,
        add_aliases: addAliases,
      });
      const parts = [];
      if (data.aliases_added) parts.push(`${data.aliases_added} alias${data.aliases_added > 1 ? "es" : ""} added`);
      toast.success(`Mapping applied${parts.length ? ` · ${parts.join(", ")}` : ""}`);
      onUpdated?.();
      setPickBank(""); setPickNet(""); setPickMode("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to apply mapping");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card p-4" data-testid="paymode-details">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">Payment mode</div>
        {needsReview && (
          <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700 bg-amber-50">
            Needs review
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-3">
        <KV label="Mode" value={txn.pg_mode_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} />
        <KV label="Sub-type / PG code" value={txn.pg_mode_subtype} mono />
        <KV label="Card network" value={txn.pg_card_network?.toUpperCase()} />
        <KV label="Card last 4" value={txn.pg_card_last4 ? `****${txn.pg_card_last4}` : "—"} mono />
        <KV label="Bank" value={txn.pg_bank_name} />
        {txn.channel_code && <KV label="Channel" value={txn.channel_code} mono />}
      </div>

      <div className="border-t border-border pt-3 mt-2">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
          Manual mapping — assign master records to this txn
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <PickMasterSelect
            label="Bank" current={txn.pg_bank_name} resolved={isResolvedBank}
            value={pickBank} onChange={setPickBank} options={masters.bank}
            testId="map-bank-select"
          />
          <PickMasterSelect
            label="Card network" current={txn.pg_card_network} resolved={isResolvedNet}
            value={pickNet} onChange={setPickNet} options={masters.card_network}
            testId="map-network-select"
          />
          <PickMasterSelect
            label="Pay mode" current={txn.pg_mode_type} resolved={isResolvedMode}
            value={pickMode} onChange={setPickMode} options={masters.pay_mode}
            testId="map-mode-select"
          />
        </div>
        <div className="flex items-center justify-between mt-3">
          <label className="text-[11px] flex items-center gap-1.5 text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox" checked={addAliases}
              onChange={(e) => setAddAliases(e.target.checked)}
              data-testid="map-add-aliases"
            />
            Also add the current raw value as an alias (auto-resolves future txns)
          </label>
          <Button
            size="sm" onClick={save}
            disabled={saving || (!pickBank && !pickNet && !pickMode)}
            data-testid="map-paymode-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Apply mapping
          </Button>
        </div>
      </div>
    </div>
  );
}

function PickMasterSelect({ label, current, resolved, value, onChange, options, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-[11px] mb-1 truncate">
        <span className="text-muted-foreground">Current: </span>
        <span className={`font-mono ${resolved ? "text-success" : "text-amber-700"}`}>
          {current || "—"}
        </span>
      </div>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm rounded-md border border-input bg-background px-2 py-1.5"
        data-testid={testId}
      >
        <option value="">— Pick master {label.toLowerCase()} —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}{o.code ? ` (${o.code})` : ""}</option>
        ))}
      </select>
    </div>
  );
}
